import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * A reusable collapsible card component that displays a titled panel which can be expanded or collapsed, optionally showing an edit action and supporting a special "password card" behavior.
 * @component
 * @selector app-collapsible-card
 * @example
 *   <app-collapsible-card
 *     [title]="'Account Details'"
 *     [isCollapsed]="false"
 *     [showEditButton]="true"
 *     [isPasswordCard]="false"
 *     [isFixedOpen]="false"
 *     (collapsedChange)="onCollapsedChange($event)"
 *     (editClicked)="onEditClicked($event)">
 *   </app-collapsible-card>
 * @input string title - The header/title text displayed on the card.
 * @input boolean isCollapsed - Controls whether the card is currently collapsed (true = collapsed). Toggled by header clicks unless isFixedOpen is true.
 * @input boolean showEditButton - When true, shows an edit button in the card header to initiate editing.
 * @input boolean isPasswordCard - When true, indicates this card holds password info; edit actions will also ensure the card expands (isCollapsed set to false).
 * @input boolean isFixedOpen - When true, prevents the card from being collapsed or toggled by user interaction.
 * @output EventEmitter<boolean> collapsedChange - Emits the new collapsed state (true/false) when the user toggles the header (not emitted when isFixedOpen prevents toggling).
 * @output EventEmitter<boolean> editClicked - Emits when the user initiates editing. Emits true when editing starts; parent can respond to open edit forms or perform other actions.
 * Key methods:
 *   - onHeaderClick(): Toggles the collapsed state unless isFixedOpen is set; emits collapsedChange with the updated state.
 *   - onEditClick(event: MouseEvent): Stops event propagation, sets internal editing state, and emits editClicked.
 *   - onEditClickpassword(event: MouseEvent): Similar to onEditClick but also forces the card to expand (isCollapsed = false) for password editing.
 *   - showEditButtonAgain(): Resets internal editing state (isEditing = false) so the edit button can be shown again.
 * Internal state:
 *   - isEditing: boolean flag tracking whether the card is currently in edit mode.
 * Lifecycle hooks:
 *   - This component does not implement special Angular lifecycle hooks; it reacts to input property changes and user interactions.
 */
@Component({
  selector: 'app-collapsible-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './collapsible-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./collapsible-card.component.scss'],
})
export class CollapsibleCardComponent {
  @Input() title = '';
  @Input() isCollapsed = false;
  @Input() showEditButton = false;
  @Input() isPasswordCard = false;
  @Input() isFixedOpen = false;
  @Output() collapsedChange = new EventEmitter<boolean>();
  @Output() editClicked = new EventEmitter<boolean>();

  isEditing = false;

  onHeaderClick(): void {
    if (this.isFixedOpen) {
      return; // Don't allow collapsing when fixed open
    }
    this.isCollapsed = !this.isCollapsed;
    this.collapsedChange.emit(this.isCollapsed);
  }

  onEditClick(event: MouseEvent): void {
    event.stopPropagation();
    this.isEditing = true;
    this.editClicked.emit(this.isEditing);
  }

  public showEditButtonAgain(): void {
    this.isEditing = false;
  }
  onEditClickpassword(event: MouseEvent): void {
    event.stopPropagation();
    this.isEditing = true;
    this.editClicked.emit(this.isEditing);
    this.isCollapsed = false;
  }
}
